/**
 * DiagnosticManagement Component
 * For company owners/admins to manage employee diagnostic requests
 * Shows a table view with employee requests, diagnosers, and form assignment
 */

import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  selectIsOwner,
  selectIsCompanyAdmin,
  selectCurrentCompany,
} from "../../store/slices/authSlice";
import {
  useGetCompanyDiagnosticRequestsQuery,
  useGetCompanyDiagnosticTemplatesQuery,
  useApproveDiagnosticRequestMutation,
  useReassignDiagnosersMutation,
  useGetCompanyMembersQuery,
  useCreateAdminDiagnosticRequestMutation,
  useAddTemplateToRequestMutation,
} from "../../services/api";
import "./DiagnosticManagement.css";

// SVG Icons
const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconX = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconUserPlus = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <line x1="20" y1="8" x2="20" y2="14" />
    <line x1="23" y1="11" x2="17" y2="11" />
  </svg>
);

const IconFile = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

export default function DiagnosticManagement() {
  const company = useSelector(selectCurrentCompany);
  const isOwner = useSelector(selectIsOwner);
  const isCompanyAdmin = useSelector(selectIsCompanyAdmin);
  const canManage = isOwner || isCompanyAdmin;

  const companyId = company?._id || company?.id;

  // State
  const [selectedTemplates, setSelectedTemplates] = useState({});
  const [editModal, setEditModal] = useState({ isOpen: false, request: null });
  const [editDiagnosers, setEditDiagnosers] = useState([]);
  const [addEmployeeModal, setAddEmployeeModal] = useState(false);
  const [addFormModal, setAddFormModal] = useState({ isOpen: false, request: null });
  const [newEmployee, setNewEmployee] = useState({
    targetId: "",
    diagnoserIds: [],
    templateId: "",
    includeSelfDiagnosis: true,
  });
  const [selectedAddFormTemplate, setSelectedAddFormTemplate] = useState("");

  // API Hooks - Now fetches both pending and approved requests
  const { data: requests = [], isLoading: requestsLoading, refetch: refetchRequests } =
    useGetCompanyDiagnosticRequestsQuery(
      { companyId }, // No status filter - gets both pending_approval and approved
      { skip: !companyId }
    );

  const { data: templates = [], isLoading: templatesLoading } =
    useGetCompanyDiagnosticTemplatesQuery(companyId, { skip: !companyId });

  const { data: membersData } = useGetCompanyMembersQuery(
    { id: companyId },
    { skip: !companyId }
  );
  const companyMembers = membersData?.members || [];

  const [approveRequest, { isLoading: isApproving }] = useApproveDiagnosticRequestMutation();
  const [reassignDiagnosers, { isLoading: isReassigning }] = useReassignDiagnosersMutation();
  const [createAdminRequest, { isLoading: isCreatingRequest }] = useCreateAdminDiagnosticRequestMutation();
  const [addTemplateToRequest, { isLoading: isAddingTemplate }] = useAddTemplateToRequestMutation();

  // If no company
  if (!company) {
    return (
      <div className="dm-page">
        <h1 className="dm-title">Manage Diagnostics</h1>
        <div className="dm-no-company">
          <p>You are not part of a company yet.</p>
        </div>
      </div>
    );
  }

  // If not owner/admin
  if (!canManage) {
    return (
      <div className="dm-page">
        <h1 className="dm-title">Manage Diagnostics</h1>
        <div className="dm-no-access">
          <p>Only company owners and admins can manage diagnostics.</p>
        </div>
      </div>
    );
  }

  // Format diagnoser names
  const formatDiagnosers = (diagnosers) => {
    if (!diagnosers || diagnosers.length === 0) return "None selected";
    return diagnosers
      .filter(d => d.status !== "removed")
      .map(d => d.user ? `${d.user.firstName} ${d.user.lastName}` : "Unknown")
      .join(", ");
  };

  // Get active diagnoser IDs
  const getActiveDiagnoserIds = (diagnosers) => {
    if (!diagnosers) return [];
    return diagnosers
      .filter(d => d.status !== "removed")
      .map(d => d.user?._id || d.user);
  };

  // Handle template selection
  const handleTemplateSelect = (requestId, templateId) => {
    setSelectedTemplates(prev => ({ ...prev, [requestId]: templateId }));
  };

  // Handle assign form (for pending requests)
  const handleAssign = async (requestId) => {
    const templateId = selectedTemplates[requestId];
    if (!templateId) {
      alert("Please select a diagnostic form first");
      return;
    }

    try {
      await approveRequest({ requestId, templateId }).unwrap();
      refetchRequests();
      setSelectedTemplates(prev => {
        const updated = { ...prev };
        delete updated[requestId];
        return updated;
      });
    } catch (error) {
      console.error("Failed to assign form:", error);
      alert(error.data?.message || "Failed to assign form");
    }
  };

  // Open edit modal
  const handleOpenEditModal = (request) => {
    const activeIds = getActiveDiagnoserIds(request.requestedDiagnosers);
    setEditDiagnosers(activeIds);
    setEditModal({ isOpen: true, request });
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setEditModal({ isOpen: false, request: null });
    setEditDiagnosers([]);
  };

  // Toggle diagnoser selection
  const handleToggleDiagnoser = (userId) => {
    setEditDiagnosers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Save diagnoser changes
  const handleSaveDiagnosers = async () => {
    if (!editModal.request) return;

    const currentIds = getActiveDiagnoserIds(editModal.request.requestedDiagnosers);
    const add = editDiagnosers.filter(id => !currentIds.includes(id));
    const remove = currentIds.filter(id => !editDiagnosers.includes(id));

    if (add.length === 0 && remove.length === 0) {
      handleCloseEditModal();
      return;
    }

    try {
      await reassignDiagnosers({
        requestId: editModal.request._id,
        add,
        remove,
      }).unwrap();
      refetchRequests();
      handleCloseEditModal();
    } catch (error) {
      console.error("Failed to update diagnosers:", error);
      alert(error.data?.message || "Failed to update diagnosers");
    }
  };

  // Get eligible diagnosers (exclude the requesting employee)
  const getEligibleMembers = (request) => {
    const requesterId = request.requestedBy?._id || request.requestedBy;
    return companyMembers.filter(m => m._id !== requesterId);
  };

  // Get employees who don't have active requests yet
  const getAvailableEmployees = () => {
    const employeesWithRequests = requests.map(r => r.requestedBy?._id || r.requestedBy);
    return companyMembers.filter(m => !employeesWithRequests.includes(m._id));
  };

  // Handle add employee modal
  const handleOpenAddEmployeeModal = () => {
    setNewEmployee({
      targetId: "",
      diagnoserIds: [],
      templateId: "",
      includeSelfDiagnosis: true,
    });
    setAddEmployeeModal(true);
  };

  const handleCloseAddEmployeeModal = () => {
    setAddEmployeeModal(false);
    setNewEmployee({
      targetId: "",
      diagnoserIds: [],
      templateId: "",
      includeSelfDiagnosis: true,
    });
  };

  const handleToggleNewDiagnoser = (userId) => {
    setNewEmployee(prev => ({
      ...prev,
      diagnoserIds: prev.diagnoserIds.includes(userId)
        ? prev.diagnoserIds.filter(id => id !== userId)
        : [...prev.diagnoserIds, userId],
    }));
  };

  const handleCreateAdminRequest = async () => {
    if (!newEmployee.targetId) {
      alert("Please select an employee");
      return;
    }
    if (!newEmployee.templateId) {
      alert("Please select a diagnostic form");
      return;
    }
    if (newEmployee.diagnoserIds.length === 0 && !newEmployee.includeSelfDiagnosis) {
      alert("Please select at least one diagnoser or include self-diagnosis");
      return;
    }

    try {
      await createAdminRequest({
        companyId,
        targetId: newEmployee.targetId,
        diagnoserIds: newEmployee.diagnoserIds,
        templateId: newEmployee.templateId,
        includeSelfDiagnosis: newEmployee.includeSelfDiagnosis,
      }).unwrap();
      refetchRequests();
      handleCloseAddEmployeeModal();
    } catch (error) {
      console.error("Failed to create diagnostic request:", error);
      alert(error.data?.message || "Failed to create diagnostic request");
    }
  };

  // Handle add form modal (for approved requests)
  const handleOpenAddFormModal = (request) => {
    setSelectedAddFormTemplate("");
    setAddFormModal({ isOpen: true, request });
  };

  const handleCloseAddFormModal = () => {
    setAddFormModal({ isOpen: false, request: null });
    setSelectedAddFormTemplate("");
  };

  const handleAddForm = async () => {
    if (!selectedAddFormTemplate) {
      alert("Please select a form");
      return;
    }

    try {
      await addTemplateToRequest({
        requestId: addFormModal.request._id,
        templateId: selectedAddFormTemplate,
      }).unwrap();
      refetchRequests();
      handleCloseAddFormModal();
    } catch (error) {
      console.error("Failed to add form:", error);
      alert(error.data?.message || "Failed to add form");
    }
  };

  // Get status badge
  const getStatusBadge = (requestStatus) => {
    if (requestStatus === "approved") {
      return <span className="dm-status-badge dm-status-active">Active</span>;
    }
    return <span className="dm-status-badge dm-status-pending">Pending</span>;
  };

  return (
    <div className="dm-page">
      <div className="dm-header">
        <h1 className="dm-title">Manage Diagnostics</h1>
        <button className="dm-add-employee-btn" onClick={handleOpenAddEmployeeModal}>
          <IconUserPlus /> Add Employee
        </button>
      </div>

      {/* Loading State */}
      {(requestsLoading || templatesLoading) && (
        <div className="dm-loading">Loading...</div>
      )}

      {/* Empty State */}
      {!requestsLoading && requests.length === 0 && (
        <div className="dm-empty">
          <p>No diagnostic requests found.</p>
          <p className="dm-empty-hint">
            Click "Add Employee" to assign diagnostics, or wait for employees to request them.
          </p>
        </div>
      )}

      {/* Requests Table */}
      {!requestsLoading && requests.length > 0 && (
        <div className="dm-table-container">
          <table className="dm-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Status</th>
                <th>Diagnosers</th>
                <th>Diagnostic Form</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => {
                const requester = request.requestedBy;
                const selectedTemplate = selectedTemplates[request._id];
                const isApproved = request.requestStatus === "approved";
                const hasTemplate = !!request.template;

                return (
                  <tr key={request._id}>
                    {/* Employee Column */}
                    <td>
                      <div className="dm-employee">
                        <span className="dm-employee-name">
                          {requester?.firstName} {requester?.lastName}
                        </span>
                        <span className="dm-employee-email">
                          {requester?.email}
                        </span>
                      </div>
                    </td>

                    {/* Status Column */}
                    <td>
                      {getStatusBadge(request.requestStatus)}
                    </td>

                    {/* Diagnosers Column */}
                    <td>
                      <div className="dm-diagnosers">
                        <span className="dm-diagnosers-list">
                          {formatDiagnosers(request.requestedDiagnosers)}
                        </span>
                        <button
                          className="dm-edit-btn"
                          onClick={() => handleOpenEditModal(request)}
                          title="Edit diagnosers"
                        >
                          <IconEdit />
                        </button>
                      </div>
                    </td>

                    {/* Diagnostic Form Column */}
                    <td>
                      {hasTemplate ? (
                        <span className="dm-assigned-form">
                          {request.template?.title || "Form assigned"}
                        </span>
                      ) : (
                        <select
                          className="dm-form-select"
                          value={selectedTemplate || ""}
                          onChange={(e) => handleTemplateSelect(request._id, e.target.value)}
                        >
                          <option value="">Select Form...</option>
                          {templates.map((t) => (
                            <option key={t._id} value={t._id}>
                              {t.title}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>

                    {/* Actions Column */}
                    <td>
                      {isApproved ? (
                        <div className="dm-action-buttons">
                          <button
                            className="dm-action-btn dm-action-add-form"
                            onClick={() => handleOpenAddFormModal(request)}
                            title="Add another form"
                          >
                            <IconFile /> <span>+Form</span>
                          </button>
                        </div>
                      ) : hasTemplate ? (
                        <span className="dm-status-assigned">Assigned</span>
                      ) : (
                        <button
                          className="dm-assign-btn"
                          disabled={!selectedTemplate || isApproving}
                          onClick={() => handleAssign(request._id)}
                        >
                          {isApproving ? "Assigning..." : "Assign"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Diagnosers Modal */}
      {editModal.isOpen && editModal.request && (
        <div className="dm-modal-overlay" onClick={handleCloseEditModal}>
          <div className="dm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dm-modal-header">
              <h2 className="dm-modal-title">Edit Diagnosers</h2>
              <button className="dm-modal-close" onClick={handleCloseEditModal}>
                <IconX />
              </button>
            </div>

            <div className="dm-modal-body">
              <p className="dm-modal-subtitle">
                Select who will diagnose {editModal.request.requestedBy?.firstName}:
              </p>

              {(() => {
                const eligibleMembers = getEligibleMembers(editModal.request);
                return (
                  <>
                    <div className="dm-member-list">
                      {eligibleMembers.map((member) => {
                        const isSelected = editDiagnosers.includes(member._id);
                        return (
                          <label key={member._id} className="dm-member-item">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleDiagnoser(member._id)}
                            />
                            <span className="dm-member-info">
                              <span className="dm-member-name">
                                {member.firstName} {member.lastName}
                              </span>
                              <span className="dm-member-email">{member.email}</span>
                            </span>
                            {isSelected && <IconCheck />}
                          </label>
                        );
                      })}
                    </div>

                    {eligibleMembers.length === 0 && (
                      <p className="dm-no-members">No other team members available.</p>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="dm-modal-footer">
              <button className="dm-modal-cancel" onClick={handleCloseEditModal}>
                Cancel
              </button>
              <button
                className="dm-modal-save"
                onClick={handleSaveDiagnosers}
                disabled={isReassigning}
              >
                {isReassigning ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {addEmployeeModal && (
        <div className="dm-modal-overlay" onClick={handleCloseAddEmployeeModal}>
          <div className="dm-modal dm-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="dm-modal-header">
              <h2 className="dm-modal-title">Add Employee Diagnostic</h2>
              <button className="dm-modal-close" onClick={handleCloseAddEmployeeModal}>
                <IconX />
              </button>
            </div>

            <div className="dm-modal-body">
              {/* Select Employee */}
              <div className="dm-form-group">
                <label className="dm-form-label">Select Employee *</label>
                <select
                  className="dm-form-select-full"
                  value={newEmployee.targetId}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, targetId: e.target.value }))}
                >
                  <option value="">Choose an employee...</option>
                  {getAvailableEmployees().map((member) => (
                    <option key={member._id} value={member._id}>
                      {member.firstName} {member.lastName} ({member.email})
                    </option>
                  ))}
                </select>
                {getAvailableEmployees().length === 0 && (
                  <p className="dm-form-hint">All employees already have active diagnostics.</p>
                )}
              </div>

              {/* Select Template */}
              <div className="dm-form-group">
                <label className="dm-form-label">Select Diagnostic Form *</label>
                <select
                  className="dm-form-select-full"
                  value={newEmployee.templateId}
                  onChange={(e) => setNewEmployee(prev => ({ ...prev, templateId: e.target.value }))}
                >
                  <option value="">Choose a form...</option>
                  {templates.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Self Diagnosis */}
              <div className="dm-form-group">
                <label className="dm-checkbox-label">
                  <input
                    type="checkbox"
                    checked={newEmployee.includeSelfDiagnosis}
                    onChange={(e) => setNewEmployee(prev => ({ ...prev, includeSelfDiagnosis: e.target.checked }))}
                  />
                  <span>Include self-diagnosis</span>
                </label>
              </div>

              {/* Select Diagnosers */}
              <div className="dm-form-group">
                <label className="dm-form-label">Select Diagnosers</label>
                <p className="dm-form-hint">Choose colleagues who will evaluate this employee</p>
                <div className="dm-member-list">
                  {companyMembers
                    .filter(m => m._id !== newEmployee.targetId)
                    .map((member) => {
                      const isSelected = newEmployee.diagnoserIds.includes(member._id);
                      return (
                        <label key={member._id} className="dm-member-item">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleNewDiagnoser(member._id)}
                          />
                          <span className="dm-member-info">
                            <span className="dm-member-name">
                              {member.firstName} {member.lastName}
                            </span>
                            <span className="dm-member-email">{member.email}</span>
                          </span>
                          {isSelected && <IconCheck />}
                        </label>
                      );
                    })}
                </div>
              </div>
            </div>

            <div className="dm-modal-footer">
              <button className="dm-modal-cancel" onClick={handleCloseAddEmployeeModal}>
                Cancel
              </button>
              <button
                className="dm-modal-save"
                onClick={handleCreateAdminRequest}
                disabled={isCreatingRequest}
              >
                {isCreatingRequest ? "Creating..." : "Create Diagnostic"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Form Modal */}
      {addFormModal.isOpen && addFormModal.request && (
        <div className="dm-modal-overlay" onClick={handleCloseAddFormModal}>
          <div className="dm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dm-modal-header">
              <h2 className="dm-modal-title">Add Additional Form</h2>
              <button className="dm-modal-close" onClick={handleCloseAddFormModal}>
                <IconX />
              </button>
            </div>

            <div className="dm-modal-body">
              <p className="dm-modal-subtitle">
                Add another diagnostic form for {addFormModal.request.requestedBy?.firstName}:
              </p>

              <div className="dm-form-group">
                <label className="dm-form-label">Select Form</label>
                <select
                  className="dm-form-select-full"
                  value={selectedAddFormTemplate}
                  onChange={(e) => setSelectedAddFormTemplate(e.target.value)}
                >
                  <option value="">Choose a form...</option>
                  {templates.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </div>

              <p className="dm-form-hint">
                This will create new assignments for all active diagnosers.
              </p>
            </div>

            <div className="dm-modal-footer">
              <button className="dm-modal-cancel" onClick={handleCloseAddFormModal}>
                Cancel
              </button>
              <button
                className="dm-modal-save"
                onClick={handleAddForm}
                disabled={isAddingTemplate || !selectedAddFormTemplate}
              >
                {isAddingTemplate ? "Adding..." : "Add Form"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
